<template>
  <div class="card">
    <div class="card-header">
      <div class="flex items-center justify-between w-full">
        <div>
          <h2 class="card-title flex items-center gap-2">
            <svg class="w-5 h-5" style="color: var(--text-tertiary);" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5h16M4 12h16M4 19h16" />
            </svg>
            Header
          </h2>
          <p class="card-description">
            Navigation bar that appears at the top of every page
          </p>
        </div>
      </div>
    </div>

    <div class="card-body">
      <!-- Tab Navigation -->
      <div class="tabs-pills mb-4">
        <button
          type="button"
          :class="['tab-pill', { active: activeTab === 'content' }]"
          @click="activeTab = 'content'">
          Content
        </button>
        <button
          type="button"
          :class="['tab-pill', { active: activeTab === 'styles' }]"
          @click="activeTab = 'styles'">
          Styles
        </button>
        <button
          type="button"
          :class="['tab-pill', { active: activeTab === 'announcement' }]"
          @click="activeTab = 'announcement'">
          Announcement
        </button>
        <button
          type="button"
          :class="['tab-pill', { active: activeTab === 'mobile' }]"
          @click="activeTab = 'mobile'">
          <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          Mobile
        </button>
      </div>

      <!-- Content Tab -->
      <div v-show="activeTab === 'content'">
        <!-- Template Selection -->
        <div class="mb-6">
          <p class="text-overline mb-2" style="color: var(--text-tertiary);">
            Template
          </p>
          <label class="form-label mb-3">Template Style</label>
          <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <label
              v-for="tmpl in templates"
              :key="templateValue(tmpl)"
              :class="[
                'template-card cursor-pointer rounded-lg border-2 p-3 transition-all hover:border-indigo-300',
                localTemplate === templateValue(tmpl) ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
              ]">
              <input
                v-model="localTemplate"
                type="radio"
                name="header_template"
                :value="templateValue(tmpl)"
                class="sr-only">
              <div class="text-center">
                <div class="text-sm font-medium" style="color: var(--text-primary);">
                  {{ templateLabel(tmpl) }}
                </div>
              </div>
            </label>
          </div>
        </div>

        <!-- Navigation Items -->
        <div class="mb-6">
          <div class="flex items-center justify-between mb-3">
            <label class="form-label mb-0">Navigation Items</label>
            <button
              type="button"
              class="btn btn-secondary btn-sm"
              @click="addNavItem">
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              Add Item
            </button>
          </div>

          <div
            ref="navItemsContainer"
            class="space-y-3">
            <div
              v-for="(item, index) in navItems"
              :key="item.id || index"
              class="nav-item-card border rounded-lg overflow-hidden">
              <!-- Main nav item row -->
              <div class="flex items-center gap-2 p-3 bg-gray-50">
                <div class="drag-handle cursor-move text-gray-400 hover:text-gray-600">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16" />
                  </svg>
                </div>

                <input
                  :value="navItemLabel(item)"
                  type="text"
                  class="form-input flex-1"
                  placeholder="Link title"
                  @input="updateNavItemLabel(item, $event.target.value)">

                <input
                  v-model="item.url"
                  type="text"
                  class="form-input flex-1"
                  placeholder="/page-url"
                  @input="emitUpdate">

                <!-- Dropdown Mode Selector -->
                <select
                  v-model="item.dropdown_mode"
                  class="form-select form-select-sm !w-24 flex-shrink-0"
                  @change="handleDropdownModeChange(item, index)">
                  <option value="none">
                    No Dropdown
                  </option>
                  <option value="simple">
                    Simple
                  </option>
                  <option value="mega">
                    Mega Menu
                  </option>
                </select>

                <button
                  type="button"
                  class="text-red-500 hover:text-red-700"
                  title="Remove item"
                  @click="removeNavItem(index)">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>

              <!-- Simple Dropdown settings (when dropdown_mode === 'simple') -->
              <div v-if="item.dropdown_mode === 'simple'" class="border-t bg-white p-3">
                <div class="flex items-center justify-between mb-2">
                  <label class="flex items-center gap-2 text-sm">
                    <input
                      v-model="item.auto_populate"
                      type="checkbox"
                      class="form-checkbox"
                      :true-value="'services'"
                      :false-value="null"
                      @change="toggleAutoPopulate(item)">
                    <span>Auto-populate from Services</span>
                  </label>
                  <button
                    v-if="item.auto_populate"
                    type="button"
                    class="btn btn-secondary btn-sm"
                    :disabled="isSyncing === item.id"
                    @click="syncNavServices(item)">
                    <svg v-if="isSyncing === item.id" class="w-4 h-4 mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <svg v-else class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Sync
                  </button>
                </div>

                <!-- Auto-populate settings -->
                <div v-if="item.auto_populate" class="grid grid-cols-2 gap-3 mt-3 p-3 bg-gray-50 rounded">
                  <div>
                    <label class="form-label text-sm">Source</label>
                    <select
                      v-model="item.auto_populate"
                      class="form-select form-select-sm"
                      @change="toggleAutoPopulate(item)">
                      <option value="services">
                        Services
                      </option>
                      <option value="service_areas">
                        Service Areas
                      </option>
                    </select>
                  </div>
                  <div>
                    <label class="form-label text-sm">Max Items</label>
                    <input
                      v-model.number="item.auto_populate_settings.max_items"
                      type="number"
                      class="form-input form-input-sm"
                      min="1"
                      max="50"
                      placeholder="No limit"
                      @input="emitUpdate">
                  </div>
                  <div class="col-span-2">
                    <label class="flex items-center gap-2 text-sm">
                      <input
                        v-model="item.auto_populate_settings.group_by_category"
                        type="checkbox"
                        class="form-checkbox"
                        @change="emitUpdate">
                      Group by category
                    </label>
                  </div>
                </div>

                <!-- Children preview -->
                <div v-if="item.children && item.children.length > 0" class="mt-3">
                  <p class="text-xs text-gray-500 mb-2">
                    {{ item.children.length }} items in dropdown
                    <span v-if="item.auto_populate" class="text-indigo-500">(auto-populated)</span>
                  </p>
                  <div class="max-h-32 overflow-y-auto space-y-1">
                    <div
                      v-for="child in item.children.slice(0, 5)"
                      :key="child.id"
                      class="flex items-center gap-2 text-sm py-1 px-2 bg-gray-50 rounded">
                      <span class="text-gray-600">{{ navItemLabel(child) }}</span>
                      <span class="text-gray-400 text-xs">{{ child.url }}</span>
                    </div>
                    <p v-if="item.children.length > 5" class="text-xs text-gray-400 px-2">
                      +{{ item.children.length - 5 }} more items
                    </p>
                  </div>
                </div>
              </div>

              <!-- Mega Menu settings (when dropdown_mode === 'mega') -->
              <div v-if="item.dropdown_mode === 'mega'" class="border-t bg-white p-3">
                <!-- Layout and Width Options -->
                <div class="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label class="form-label text-sm">Layout</label>
                    <select
                      v-model="item.mega_menu.layout"
                      class="form-select form-select-sm"
                      @change="emitUpdate">
                      <option value="2-col">
                        2 Columns
                      </option>
                      <option value="3-col">
                        3 Columns
                      </option>
                      <option value="4-col">
                        4 Columns
                      </option>
                      <option value="2-col-featured">
                        2 Columns + Featured
                      </option>
                    </select>
                  </div>
                  <div>
                    <label class="form-label text-sm">Width</label>
                    <select
                      v-model="item.mega_menu.width"
                      class="form-select form-select-sm"
                      @change="emitUpdate">
                      <option value="auto">
                        Auto
                      </option>
                      <option value="container">
                        Container
                      </option>
                      <option value="full">
                        Full Width
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Mega Menu Columns -->
                <div class="mb-4">
                  <div class="flex items-center justify-between mb-2">
                    <label class="form-label text-sm mb-0">Columns</label>
                    <button
                      type="button"
                      class="btn btn-secondary btn-xs"
                      @click="addMegaColumn(item)">
                      + Add Column
                    </button>
                  </div>

                  <div class="space-y-3">
                    <div
                      v-for="(column, colIdx) in item.mega_menu.columns"
                      :key="column.id"
                      class="border rounded-lg p-3 bg-gray-50">
                      <div class="flex items-center gap-2 mb-2">
                        <input
                          v-model="column.title"
                          type="text"
                          class="form-input form-input-sm flex-1"
                          placeholder="Column Title"
                          @input="emitUpdate">
                        <button
                          type="button"
                          class="text-red-400 hover:text-red-600"
                          @click="removeMegaColumn(item, colIdx)">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>

                      <!-- Column Links -->
                      <div class="space-y-1 ml-2">
                        <div
                          v-for="(link, linkIdx) in column.links"
                          :key="link.id || linkIdx"
                          class="flex items-center gap-2">
                          <input
                            v-model="link.title"
                            type="text"
                            class="form-input form-input-xs flex-1"
                            placeholder="Link title"
                            @input="emitUpdate">
                          <input
                            v-model="link.url"
                            type="text"
                            class="form-input form-input-xs flex-1"
                            placeholder="/url"
                            @input="emitUpdate">
                          <button
                            type="button"
                            class="text-red-300 hover:text-red-500"
                            @click="removeMegaLink(item, colIdx, linkIdx)">
                            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <button
                          type="button"
                          class="text-xs text-indigo-500 hover:text-indigo-700"
                          @click="addMegaLink(item, colIdx)">
                          + Add link
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Featured Section Toggle -->
                <div class="mb-4 p-3 border rounded-lg">
                  <label class="flex items-center gap-2 text-sm mb-3">
                    <input
                      v-model="item.mega_menu.featured.enabled"
                      type="checkbox"
                      class="form-checkbox"
                      @change="emitUpdate">
                    <span class="font-medium">Featured Section</span>
                  </label>

                  <div v-if="item.mega_menu.featured.enabled" class="grid grid-cols-2 gap-3">
                    <div class="col-span-2">
                      <label class="form-label text-xs">Image URL</label>
                      <input
                        v-model="item.mega_menu.featured.image_url"
                        type="text"
                        class="form-input form-input-sm"
                        placeholder="https://..."
                        @input="emitUpdate">
                    </div>
                    <div>
                      <label class="form-label text-xs">Title</label>
                      <input
                        v-model="item.mega_menu.featured.title"
                        type="text"
                        class="form-input form-input-sm"
                        placeholder="Summer Special"
                        @input="emitUpdate">
                    </div>
                    <div>
                      <label class="form-label text-xs">Description</label>
                      <input
                        v-model="item.mega_menu.featured.description"
                        type="text"
                        class="form-input form-input-sm"
                        placeholder="20% off all services"
                        @input="emitUpdate">
                    </div>
                    <div>
                      <label class="form-label text-xs">CTA Text</label>
                      <input
                        v-model="item.mega_menu.featured.cta.text"
                        type="text"
                        class="form-input form-input-sm"
                        placeholder="Learn More"
                        @input="emitUpdate">
                    </div>
                    <div>
                      <label class="form-label text-xs">CTA URL</label>
                      <input
                        v-model="item.mega_menu.featured.cta.url"
                        type="text"
                        class="form-input form-input-sm"
                        placeholder="/specials"
                        @input="emitUpdate">
                    </div>
                  </div>
                </div>

                <!-- Footer Section Toggle -->
                <div class="p-3 border rounded-lg">
                  <label class="flex items-center gap-2 text-sm mb-3">
                    <input
                      v-model="item.mega_menu.footer.enabled"
                      type="checkbox"
                      class="form-checkbox"
                      @change="emitUpdate">
                    <span class="font-medium">Footer Row</span>
                  </label>

                  <div v-if="item.mega_menu.footer.enabled" class="grid grid-cols-3 gap-3">
                    <div class="col-span-1">
                      <label class="form-label text-xs">Text</label>
                      <input
                        v-model="item.mega_menu.footer.text"
                        type="text"
                        class="form-input form-input-sm"
                        placeholder="Can't find what you need?"
                        @input="emitUpdate">
                    </div>
                    <div>
                      <label class="form-label text-xs">CTA Text</label>
                      <input
                        v-model="item.mega_menu.footer.cta.text"
                        type="text"
                        class="form-input form-input-sm"
                        placeholder="Contact Us"
                        @input="emitUpdate">
                    </div>
                    <div>
                      <label class="form-label text-xs">CTA URL</label>
                      <input
                        v-model="item.mega_menu.footer.cta.url"
                        type="text"
                        class="form-input form-input-sm"
                        placeholder="/contact"
                        @input="emitUpdate">
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p
            v-if="navItems.length === 0"
            class="text-sm text-center py-4"
            style="color: var(--text-secondary);">
            No navigation items. Click "Add Item" to create one.
          </p>
        </div>

        <!-- CTA Button -->
        <div class="mb-6">
          <label class="form-label">CTA Button</label>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="form-label text-sm">Button Text</label>
              <input
                v-model="localContent.primary_cta.text"
                type="text"
                class="form-input"
                placeholder="Get a Quote"
                @input="emitUpdate">
            </div>
            <div>
              <label class="form-label text-sm">Button URL</label>
              <input
                v-model="localContent.primary_cta.url"
                type="text"
                class="form-input"
                placeholder="/contact"
                @input="emitUpdate">
            </div>
          </div>
        </div>

        <!-- Top Bar Settings -->
        <div class="mb-6">
          <label class="form-label">Top Bar</label>
          <div class="space-y-3">
            <label class="flex items-center gap-2">
              <input
                v-model="localContent.show_top_bar"
                type="checkbox"
                class="form-checkbox"
                @change="emitUpdate">
              <span class="text-sm">Show top bar</span>
            </label>

            <div v-if="localContent.show_top_bar" class="grid grid-cols-2 gap-4">
              <div>
                <label class="form-label text-sm">Phone Number</label>
                <input
                  v-model="localContent.phone"
                  type="text"
                  class="form-input"
                  placeholder="(555) 123-4567"
                  @input="emitUpdate">
              </div>
              <div>
                <label class="form-label text-sm">Tagline</label>
                <input
                  v-model="localContent.tagline"
                  type="text"
                  class="form-input"
                  placeholder="Licensed & Insured"
                  @input="emitUpdate">
              </div>
            </div>
          </div>
        </div>

        <!-- Sticky Header -->
        <div class="mb-6">
          <label class="flex items-center gap-2">
            <input
              v-model="localContent.sticky"
              type="checkbox"
              class="form-checkbox"
              @change="emitUpdate">
            <span class="text-sm">Sticky header (stays at top when scrolling)</span>
          </label>
        </div>
      </div>

      <!-- Styles Tab -->
      <div v-show="activeTab === 'styles'">
        <!-- Style Presets -->
        <div class="mb-6">
          <label class="form-label">Quick Presets</label>
          <p class="text-sm text-gray-500 mb-3">
            Apply a complete style in one click
          </p>
          <div class="grid grid-cols-5 gap-2">
            <button
              v-for="preset in stylePresets"
              :key="preset.value"
              type="button"
              :class="[
                'flex flex-col items-center p-3 rounded-lg border-2 text-sm transition-all',
                activePreset === preset.value
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 hover:border-gray-300'
              ]"
              :disabled="isApplyingPreset"
              @click="applyPreset(preset.value)">
              <span class="text-2xl mb-1">{{ preset.icon }}</span>
              <span class="font-medium">{{ preset.label }}</span>
            </button>
          </div>
        </div>

        <hr class="my-6 border-gray-200">

        <!-- Background Type -->
        <div class="mb-6">
          <label class="form-label">Background</label>
          <div class="flex flex-wrap gap-2 mb-3">
            <button
              v-for="type in backgroundTypeOptions"
              :key="type.value"
              type="button"
              :class="[
                'px-3 py-2 rounded-lg border text-sm transition-all',
                localSettings.background_type === type.value
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 hover:border-gray-300'
              ]"
              @click="updateSetting('background_type', type.value)">
              {{ type.label }}
            </button>
          </div>

          <!-- Solid color options -->
          <div v-if="localSettings.background_type === 'solid'" class="flex flex-wrap gap-2">
            <button
              v-for="bg in backgroundOptions"
              :key="bg.value"
              type="button"
              :class="[
                'px-3 py-2 rounded-lg border text-sm transition-all',
                localSettings.background_color === bg.value
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 hover:border-gray-300'
              ]"
              @click="updateSetting('background_color', bg.value)">
              {{ bg.label }}
            </button>
          </div>

          <!-- Gradient options -->
          <div v-if="localSettings.background_type === 'gradient'" class="grid grid-cols-3 gap-4">
            <div>
              <label class="form-label text-sm">From Color</label>
              <input
                v-model="localSettings.gradient_from"
                type="color"
                class="w-full h-10 rounded border cursor-pointer"
                @input="emitUpdate">
            </div>
            <div>
              <label class="form-label text-sm">To Color</label>
              <input
                v-model="localSettings.gradient_to"
                type="color"
                class="w-full h-10 rounded border cursor-pointer"
                @input="emitUpdate">
            </div>
            <div>
              <label class="form-label text-sm">Direction</label>
              <select
                v-model="localSettings.gradient_direction"
                class="form-select"
                @change="emitUpdate">
                <option value="to-r">
                  Left to Right
                </option>
                <option value="to-l">
                  Right to Left
                </option>
                <option value="to-b">
                  Top to Bottom
                </option>
                <option value="to-t">
                  Bottom to Top
                </option>
                <option value="to-br">
                  Diagonal ↘
                </option>
                <option value="to-bl">
                  Diagonal ↙
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Shadow -->
        <div class="mb-6">
          <label class="form-label">Shadow</label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="shadow in shadowOptions"
              :key="shadow.value"
              type="button"
              :class="[
                'px-3 py-2 rounded-lg border text-sm transition-all',
                localSettings.shadow === shadow.value
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 hover:border-gray-300'
              ]"
              @click="updateSetting('shadow', shadow.value)">
              {{ shadow.label }}
            </button>
          </div>
        </div>

        <!-- Navigation Link Style -->
        <div class="mb-6">
          <label class="form-label">Navigation Links</label>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="form-label text-sm">Text Color</label>
              <select
                v-model="localSettings.nav_link_color"
                class="form-select"
                @change="emitUpdate">
                <option value="text">
                  Default (Gray)
                </option>
                <option value="heading">
                  Dark
                </option>
                <option value="primary">
                  Primary Color
                </option>
                <option value="inverted">
                  White
                </option>
              </select>
            </div>
            <div>
              <label class="form-label text-sm">Font Weight</label>
              <select
                v-model="localSettings.nav_link_font_weight"
                class="form-select"
                @change="emitUpdate">
                <option value="normal">
                  Normal
                </option>
                <option value="medium">
                  Medium
                </option>
                <option value="semibold">
                  Semi-bold
                </option>
                <option value="bold">
                  Bold
                </option>
              </select>
            </div>
            <div>
              <label class="form-label text-sm">Font Size</label>
              <select
                v-model="localSettings.nav_link_font_size"
                class="form-select"
                @change="emitUpdate">
                <option value="sm">
                  Small
                </option>
                <option value="base">
                  Normal
                </option>
                <option value="lg">
                  Large
                </option>
              </select>
            </div>
            <div>
              <label class="form-label text-sm">Spacing</label>
              <select
                v-model="localSettings.nav_link_spacing"
                class="form-select"
                @change="emitUpdate">
                <option value="tight">
                  Tight
                </option>
                <option value="normal">
                  Normal
                </option>
                <option value="loose">
                  Loose
                </option>
              </select>
            </div>
          </div>
          <label class="flex items-center gap-2 mt-3">
            <input
              v-model="localSettings.nav_link_uppercase"
              type="checkbox"
              class="form-checkbox"
              @change="emitUpdate">
            <span class="text-sm">Uppercase Letters</span>
          </label>
        </div>

        <!-- CTA Button Style -->
        <div class="mb-6">
          <label class="form-label">CTA Button Style</label>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="form-label text-sm">Variant</label>
              <select
                v-model="localSettings.cta_variant"
                class="form-select"
                @change="emitUpdate">
                <option value="primary">
                  Primary
                </option>
                <option value="secondary">
                  Secondary
                </option>
                <option value="accent">
                  Accent
                </option>
                <option value="outline">
                  Outline
                </option>
                <option value="ghost">
                  Ghost
                </option>
                <option value="white">
                  White
                </option>
              </select>
            </div>
            <div>
              <label class="form-label text-sm">Size</label>
              <select
                v-model="localSettings.cta_size"
                class="form-select"
                @change="emitUpdate">
                <option value="sm">
                  Small
                </option>
                <option value="md">
                  Medium
                </option>
                <option value="lg">
                  Large
                </option>
                <option value="xl">
                  Extra Large
                </option>
              </select>
            </div>
            <div>
              <label class="form-label text-sm">Corners</label>
              <select
                v-model="localSettings.cta_rounded"
                class="form-select"
                @change="emitUpdate">
                <option value="default">
                  Default
                </option>
                <option value="full">
                  Pill Shape
                </option>
                <option value="none">
                  Square
                </option>
              </select>
            </div>
            <div>
              <label class="form-label text-sm">Hover Effect</label>
              <select
                v-model="localSettings.cta_hover_effect"
                class="form-select"
                @change="emitUpdate">
                <option value="none">
                  None
                </option>
                <option value="lift">
                  Lift Up
                </option>
                <option value="glow">
                  Glow
                </option>
                <option value="darken">
                  Darken
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Logo Settings -->
        <div class="mb-6">
          <label class="form-label">Logo</label>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="form-label text-sm">Size</label>
              <select
                v-model="localSettings.logo_size"
                class="form-select"
                @change="emitUpdate">
                <option value="sm">
                  Small
                </option>
                <option value="md">
                  Medium
                </option>
                <option value="lg">
                  Large
                </option>
                <option value="xl">
                  Extra Large
                </option>
              </select>
            </div>
            <div>
              <label class="form-label text-sm">Alignment</label>
              <select
                v-model="localSettings.logo_alignment"
                class="form-select"
                @change="emitUpdate">
                <option value="left">
                  Left
                </option>
                <option value="center">
                  Center
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Announcement Tab -->
      <div v-show="activeTab === 'announcement'">
        <!-- Enable Toggle -->
        <div class="mb-6 p-4 bg-gray-50 rounded-lg">
          <label class="flex items-center justify-between">
            <div>
              <span class="font-medium">Enable Announcement Bar</span>
              <p class="text-sm text-gray-500 mt-1">Display a promotional bar above or below the header</p>
            </div>
            <div class="relative">
              <input
                v-model="announcementBar.enabled"
                type="checkbox"
                class="sr-only peer"
                @change="emitUpdate">
              <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600" />
            </div>
          </label>
        </div>

        <!-- Announcement Settings (shown when enabled) -->
        <div v-if="announcementBar.enabled" class="space-y-6">
          <!-- Preview -->
          <div class="mb-6">
            <label class="form-label text-sm mb-2">Preview</label>
            <div
              :class="[
                'p-3 rounded-lg text-center text-sm flex items-center justify-center gap-2',
                announcementBarPreviewClasses
              ]"
              :style="announcementBarPreviewStyles">
              <component :is="announcementIconComponent" v-if="announcementBar.icon !== 'none'" class="w-4 h-4" />
              <span>{{ announcementBar.text || 'Your announcement message here...' }}</span>
              <a v-if="announcementBar.link_text" href="#" class="underline font-medium ml-1">{{ announcementBar.link_text }}</a>
              <button v-if="announcementBar.dismissible" class="ml-2 opacity-75 hover:opacity-100">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Text Content -->
          <div class="mb-6">
            <label class="form-label">Announcement Text</label>
            <textarea
              v-model="announcementBar.text"
              class="form-input"
              rows="2"
              maxlength="150"
              placeholder="Special offer: 10% off all services this month!"
              @input="emitUpdate" />
            <p class="text-xs text-gray-400 mt-1">
              {{ (announcementBar.text || '').length }}/150 characters
            </p>
          </div>

          <!-- Link Configuration -->
          <div class="mb-6">
            <label class="form-label">Link (Optional)</label>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="form-label text-sm">Link Text</label>
                <input
                  v-model="announcementBar.link_text"
                  type="text"
                  class="form-input"
                  placeholder="Learn more"
                  @input="emitUpdate">
              </div>
              <div>
                <label class="form-label text-sm">Link URL</label>
                <input
                  v-model="announcementBar.link_url"
                  type="text"
                  class="form-input"
                  placeholder="/specials"
                  @input="emitUpdate">
              </div>
            </div>
          </div>

          <!-- Background Color -->
          <div class="mb-6">
            <label class="form-label">Background Color</label>
            <div class="flex flex-wrap gap-2 mb-3">
              <button
                v-for="color in announcementColorOptions"
                :key="color.value"
                type="button"
                :class="[
                  'px-3 py-2 rounded-lg border text-sm transition-all',
                  announcementBar.background_color === color.value
                    ? 'border-indigo-500 ring-2 ring-indigo-200'
                    : 'border-gray-200 hover:border-gray-300'
                ]"
                :style="{ backgroundColor: color.preview }"
                @click="updateAnnouncementSetting('background_color', color.value)">
                <span :class="color.textClass">{{ color.label }}</span>
              </button>
            </div>

            <!-- Custom color picker -->
            <div v-if="announcementBar.background_color === 'custom'" class="flex items-center gap-3">
              <label class="form-label text-sm mb-0">Custom Color:</label>
              <input
                v-model="announcementBar.custom_bg_color"
                type="color"
                class="w-12 h-8 rounded border cursor-pointer"
                @input="emitUpdate">
              <input
                v-model="announcementBar.custom_bg_color"
                type="text"
                class="form-input form-input-sm w-28"
                placeholder="#ff6600"
                @input="emitUpdate">
            </div>
          </div>

          <!-- Text Color -->
          <div class="mb-6">
            <label class="form-label">Text Color</label>
            <div class="flex gap-2">
              <button
                v-for="textColor in ['white', 'dark', 'auto']"
                :key="textColor"
                type="button"
                :class="[
                  'px-3 py-2 rounded-lg border text-sm transition-all capitalize',
                  announcementBar.text_color === textColor
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 hover:border-gray-300'
                ]"
                @click="updateAnnouncementSetting('text_color', textColor)">
                {{ textColor }}
              </button>
            </div>
          </div>

          <!-- Icon -->
          <div class="mb-6">
            <label class="form-label">Icon</label>
            <div class="flex gap-2">
              <button
                v-for="icon in announcementIconOptions"
                :key="icon.value"
                type="button"
                :class="[
                  'flex items-center gap-1 px-3 py-2 rounded-lg border text-sm transition-all',
                  announcementBar.icon === icon.value
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 hover:border-gray-300'
                ]"
                @click="updateAnnouncementSetting('icon', icon.value)">
                <component :is="icon.component" v-if="icon.component" class="w-4 h-4" />
                {{ icon.label }}
              </button>
            </div>
          </div>

          <!-- Position -->
          <div class="mb-6">
            <label class="form-label">Position</label>
            <div class="flex gap-2">
              <button
                v-for="pos in [{ value: 'above_header', label: 'Above Header' }, { value: 'below_header', label: 'Below Header' }]"
                :key="pos.value"
                type="button"
                :class="[
                  'px-3 py-2 rounded-lg border text-sm transition-all',
                  announcementBar.position === pos.value
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 hover:border-gray-300'
                ]"
                @click="updateAnnouncementSetting('position', pos.value)">
                {{ pos.label }}
              </button>
            </div>
          </div>

          <!-- Dismissible Settings -->
          <div class="mb-6 p-4 bg-gray-50 rounded-lg">
            <label class="flex items-center gap-2 mb-3">
              <input
                v-model="announcementBar.dismissible"
                type="checkbox"
                class="form-checkbox"
                @change="emitUpdate">
              <span class="text-sm font-medium">Allow users to dismiss</span>
            </label>

            <div v-if="announcementBar.dismissible" class="flex items-center gap-2 ml-6">
              <label class="text-sm text-gray-600">Remember for</label>
              <input
                v-model.number="announcementBar.cookie_duration"
                type="number"
                min="1"
                max="365"
                class="form-input form-input-sm w-20"
                @input="emitUpdate">
              <span class="text-sm text-gray-600">days</span>
            </div>
          </div>

          <!-- Scheduling -->
          <div class="mb-6">
            <label class="form-label">Scheduling (Optional)</label>
            <p class="text-sm text-gray-500 mb-3">
              Leave empty for no date restrictions
            </p>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="form-label text-sm">Start Date</label>
                <input
                  v-model="announcementBar.start_date"
                  type="datetime-local"
                  class="form-input"
                  @change="emitUpdate">
              </div>
              <div>
                <label class="form-label text-sm">End Date</label>
                <input
                  v-model="announcementBar.end_date"
                  type="datetime-local"
                  class="form-input"
                  @change="emitUpdate">
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Tab -->
      <div v-show="activeTab === 'mobile'">
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div class="flex items-start gap-3">
            <svg class="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <div>
              <p class="text-sm font-medium text-blue-800">
                Mobile-Specific Settings
              </p>
              <p class="text-sm text-blue-600">
                These settings only affect how your header appears on mobile devices (screens smaller than 1024px).
              </p>
            </div>
          </div>
        </div>

        <!-- Mobile Menu Style -->
        <div class="mb-6">
          <label class="form-label">Mobile Menu Style</label>
          <p class="text-sm text-gray-500 mb-3">
            How the mobile menu opens when users tap the menu button
          </p>
          <div class="grid grid-cols-3 gap-3">
            <button
              v-for="style in mobileMenuStyles"
              :key="style.value"
              type="button"
              :class="[
                'flex flex-col items-center p-4 rounded-lg border-2 transition-all',
                mobileSettings.menu_style === style.value
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 hover:border-gray-300'
              ]"
              @click="updateMobileSetting('menu_style', style.value)">
              <span class="text-2xl mb-2">{{ style.icon }}</span>
              <span class="text-sm font-medium">{{ style.label }}</span>
              <span class="text-xs text-gray-500 text-center mt-1">{{ style.description }}</span>
            </button>
          </div>
        </div>

        <!-- Mobile Logo Size -->
        <div class="mb-6">
          <label class="form-label">Mobile Logo Size</label>
          <div class="flex gap-2">
            <button
              v-for="size in ['sm', 'md', 'lg']"
              :key="size"
              type="button"
              :class="[
                'px-4 py-2 rounded-lg border text-sm transition-all uppercase',
                mobileSettings.logo_size === size
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 hover:border-gray-300'
              ]"
              @click="updateMobileSetting('logo_size', size)">
              {{ size }}
            </button>
          </div>
        </div>

        <!-- Mobile Top Bar -->
        <div class="mb-6 p-4 bg-gray-50 rounded-lg">
          <label class="flex items-center gap-2">
            <input
              v-model="mobileSettings.show_top_bar"
              type="checkbox"
              class="form-checkbox"
              @change="emitUpdate">
            <span class="text-sm">Show top bar on mobile</span>
          </label>
          <p class="text-xs text-gray-500 mt-1 ml-6">
            By default, the top bar with phone/tagline is hidden on mobile to save space
          </p>
        </div>

        <!-- Mobile CTA -->
        <div class="mb-6">
          <label class="form-label">Mobile CTA Button</label>
          <div class="p-4 bg-gray-50 rounded-lg">
            <label class="flex items-center gap-2 mb-4">
              <input
                v-model="mobileSettings.use_custom_cta"
                type="checkbox"
                class="form-checkbox"
                @change="emitUpdate">
              <span class="text-sm">Use different CTA on mobile</span>
            </label>

            <div v-if="mobileSettings.use_custom_cta" class="space-y-4">
              <div class="grid grid-cols-3 gap-4">
                <div>
                  <label class="form-label text-sm">Action Type</label>
                  <select
                    v-model="mobileSettings.cta.action_type"
                    class="form-select"
                    @change="emitUpdate">
                    <option value="link">
                      Link
                    </option>
                    <option value="phone">
                      Phone Call
                    </option>
                    <option value="anchor">
                      Scroll to Section
                    </option>
                  </select>
                </div>
                <div>
                  <label class="form-label text-sm">Button Text</label>
                  <input
                    v-model="mobileSettings.cta.text"
                    type="text"
                    class="form-input"
                    placeholder="Call Now"
                    @input="emitUpdate">
                </div>
                <div>
                  <label class="form-label text-sm">
                    {{ mobileSettings.cta.action_type === 'phone' ? 'Phone Number' : 'URL/Anchor' }}
                  </label>
                  <input
                    v-model="mobileSettings.cta.action_value"
                    type="text"
                    class="form-input"
                    :placeholder="mobileSettings.cta.action_type === 'phone' ? '(555) 123-4567' : '/contact'"
                    @input="emitUpdate">
                </div>
              </div>
              <p class="text-xs text-gray-500">
                Tip: "Call Now" with phone action is popular for mobile users
              </p>
            </div>
          </div>
        </div>

        <!-- Hidden Nav Items on Mobile -->
        <div class="mb-6">
          <label class="form-label">Navigation Items Visibility</label>
          <p class="text-sm text-gray-500 mb-3">
            Uncheck items to hide them on mobile while keeping them visible on desktop
          </p>
          <div class="space-y-2 p-4 bg-gray-50 rounded-lg max-h-48 overflow-y-auto">
            <label
              v-for="item in navItems"
              :key="item.id"
              class="flex items-center gap-2">
              <input
                :checked="!mobileSettings.hidden_nav_items.includes(item.id)"
                type="checkbox"
                class="form-checkbox"
                @change="toggleMobileNavItem(item.id)">
              <span class="text-sm">{{ navItemLabel(item) || 'Untitled Item' }}</span>
            </label>
            <p v-if="navItems.length === 0" class="text-sm text-gray-400">
              No navigation items to configure
            </p>
          </div>
        </div>

        <!-- Mobile Phone Display -->
        <div class="mb-6 p-4 bg-gray-50 rounded-lg">
          <label class="flex items-center gap-2">
            <input
              v-model="mobileSettings.show_phone"
              type="checkbox"
              class="form-checkbox"
              @change="emitUpdate">
            <span class="text-sm">Show clickable phone number in mobile menu</span>
          </label>
          <p class="text-xs text-gray-500 mt-1 ml-6">
            Adds a prominent tap-to-call button in the mobile navigation
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick, inject } from 'vue'
import { useDragDrop } from './composables/useDragDrop'

const props = defineProps({
  content: { type: Object, required: true },
  settings: { type: Object, required: true },
  template: { type: String, required: true },
  templates: { type: Array, default: () => []},
  services: { type: Array, default: () => []},
  serviceAreas: { type: Array, default: () => []},
  businessName: { type: String, default: '' },
  logoUrl: { type: String, default: '' }
})

const emit = defineEmits(['update:content', 'update:settings', 'update:template', 'update'])

// Inject API endpoints from parent
const apiEndpoints = inject('apiEndpoints', {})

function templateValue(tmpl) {
  return tmpl?.value || tmpl?.type || ''
}

function templateLabel(tmpl) {
  return tmpl?.label || tmpl?.name || ''
}

function navItemLabel(item) {
  return item?.title || item?.label || ''
}

function updateNavItemLabel(item, value) {
  if (!item) {
    return
  }
  item.title = value
  item.label = value
  emitUpdate()
}

function normalizeNavItems(items) {
  if (!Array.isArray(items)) {
    return []
  }

  return items.map((item) => {
    if (!item || typeof item !== 'object') {
      return {
        title: item ? String(item) : '',
        label: item ? String(item) : '',
        url: '/'
      }
    }

    const normalized = { ...item }

    if (!normalized.title && normalized.label) {
      normalized.title = normalized.label
    }
    if (!normalized.label && normalized.title) {
      normalized.label = normalized.title
    }

    if (Array.isArray(normalized.children)) {
      normalized.children = normalizeNavItems(normalized.children)
    }

    if (normalized.mega_menu && Array.isArray(normalized.mega_menu.columns)) {
      normalized.mega_menu = {
        ...normalized.mega_menu,
        columns: normalized.mega_menu.columns.map((column) => {
          const normalizedColumn = { ...column }
          if (!normalizedColumn.title && normalizedColumn.label) {
            normalizedColumn.title = normalizedColumn.label
          }
          if (Array.isArray(normalizedColumn.links)) {
            normalizedColumn.links = normalizedColumn.links.map((link) => {
              const normalizedLink = { ...link }
              if (!normalizedLink.title && normalizedLink.label) {
                normalizedLink.title = normalizedLink.label
              }
              if (!normalizedLink.label && normalizedLink.title) {
                normalizedLink.label = normalizedLink.title
              }
              return normalizedLink
            })
          }
          return normalizedColumn
        })
      }
    }

    return normalized
  })
}

function normalizeHeaderContent(content) {
  return {
    ...content,
    nav_items: normalizeNavItems(content.nav_items || [])
  }
}

// Local state
const activeTab = ref('content')
const navItemsContainer = ref(null)
const isApplyingPreset = ref(false)
const activePreset = ref(null)

// Local copies for v-model
const localContent = ref(normalizeHeaderContent({
  nav_items: [],
  primary_cta: { text: 'Get a Quote', url: '/contact' },
  show_top_bar: true,
  phone: '',
  tagline: '',
  sticky: true,
  ...props.content
}))

const localSettings = ref({
  background_type: 'solid',
  background_color: 'white',
  gradient_from: '#4f46e5',
  gradient_to: '#7c3aed',
  gradient_direction: 'to-r',
  shadow: 'md',
  cta_variant: 'primary',
  cta_size: 'md',
  cta_rounded: 'default',
  cta_hover_effect: 'none',
  nav_link_color: 'text',
  nav_link_font_weight: 'medium',
  nav_link_font_size: 'base',
  nav_link_spacing: 'normal',
  nav_link_uppercase: false,
  logo_size: 'md',
  logo_alignment: 'left',
  ...props.settings
})

const localTemplate = ref(props.template)

// Announcement bar state
const announcementBar = ref({
  enabled: false,
  text: '',
  link_text: '',
  link_url: '',
  background_color: 'primary',
  custom_bg_color: '#ff6600',
  text_color: 'white',
  dismissible: true,
  cookie_duration: 7,
  start_date: null,
  end_date: null,
  icon: 'megaphone',
  position: 'above_header',
  ...props.content.announcement_bar
})

// Announcement color options
const announcementColorOptions = [
  { value: 'primary', label: 'Primary', preview: 'var(--color-primary, #4f46e5)', textClass: 'text-white' },
  { value: 'secondary', label: 'Secondary', preview: 'var(--color-secondary, #6b7280)', textClass: 'text-white' },
  { value: 'accent', label: 'Accent', preview: 'var(--color-accent, #ec4899)', textClass: 'text-white' },
  { value: 'warning', label: 'Warning', preview: '#f59e0b', textClass: 'text-white' },
  { value: 'info', label: 'Info', preview: '#3b82f6', textClass: 'text-white' },
  { value: 'dark', label: 'Dark', preview: '#1f2937', textClass: 'text-white' },
  { value: 'custom', label: 'Custom', preview: '#e5e7eb', textClass: 'text-gray-800' }
]

// Announcement icon options
const announcementIconOptions = [
  { value: 'none', label: 'None', component: null },
  { value: 'megaphone', label: 'Megaphone', component: 'MegaphoneIcon' },
  { value: 'tag', label: 'Tag', component: 'TagIcon' },
  { value: 'clock', label: 'Clock', component: 'ClockIcon' },
  { value: 'star', label: 'Star', component: 'StarIcon' }
]

// Computed preview classes for announcement bar
const announcementBarPreviewClasses = computed(() => {
  const textColor = announcementBar.value.text_color
  return textColor === 'dark' ? 'text-gray-900' : 'text-white'
})

// Computed preview styles for announcement bar
const announcementBarPreviewStyles = computed(() => {
  const bg = announcementBar.value.background_color
  const customBg = announcementBar.value.custom_bg_color

  let backgroundColor
  switch (bg) {
    case 'primary': backgroundColor = 'var(--color-primary, #4f46e5)'; break
    case 'secondary': backgroundColor = 'var(--color-secondary, #6b7280)'; break
    case 'accent': backgroundColor = 'var(--color-accent, #ec4899)'; break
    case 'warning': backgroundColor = '#f59e0b'; break
    case 'info': backgroundColor = '#3b82f6'; break
    case 'dark': backgroundColor = '#1f2937'; break
    case 'custom': backgroundColor = customBg || '#ff6600'; break
    default: backgroundColor = 'var(--color-primary, #4f46e5)'
  }

  return { backgroundColor }
})

// Icon component mapping
const announcementIconComponent = computed(() => {
  const iconMap = {
    megaphone: 'MegaphoneIcon',
    tag: 'TagIcon',
    clock: 'ClockIcon',
    star: 'StarIcon'
  }
  return iconMap[announcementBar.value.icon] || null
})

// Update announcement setting
function updateAnnouncementSetting(key, value) {
  announcementBar.value[key] = value
  emitUpdate()
}

// Mobile settings state
const mobileSettings = ref({
  menu_style: 'dropdown',
  show_phone: true,
  show_top_bar: false,
  use_custom_cta: false,
  cta: {
    text: 'Call Now',
    action_type: 'phone',
    action_value: ''
  },
  hidden_nav_items: [],
  logo_size: 'md',
  ...props.content.mobile
})

// Mobile menu style options
const mobileMenuStyles = [
  { value: 'dropdown', label: 'Dropdown', icon: '↓', description: 'Drops down below header' },
  { value: 'slide', label: 'Slide', icon: '←', description: 'Slides in from side' },
  { value: 'overlay', label: 'Overlay', icon: '▢', description: 'Full-screen overlay' }
]

// Update mobile setting
function updateMobileSetting(key, value) {
  mobileSettings.value[key] = value
  emitUpdate()
}

// Toggle mobile nav item visibility
function toggleMobileNavItem(itemId) {
  const hiddenItems = mobileSettings.value.hidden_nav_items
  const index = hiddenItems.indexOf(itemId)

  if (index === -1) {
    hiddenItems.push(itemId)
  } else {
    hiddenItems.splice(index, 1)
  }
  emitUpdate()
}

// Navigation items computed
const navItems = computed({
  get: () => localContent.value.nav_items || [],
  set: (val) => {
    localContent.value.nav_items = val
    emitUpdate()
  }
})

// Style presets
const stylePresets = [
  { value: 'professional', label: 'Professional', icon: '💼' },
  { value: 'bold', label: 'Bold', icon: '🎨' },
  { value: 'minimal', label: 'Minimal', icon: '✨' },
  { value: 'dark', label: 'Dark', icon: '🌙' },
  { value: 'light', label: 'Light', icon: '☀️' }
]

// Background type options
const backgroundTypeOptions = [
  { value: 'solid', label: 'Solid Color' },
  { value: 'gradient', label: 'Gradient' },
  { value: 'transparent', label: 'Transparent' }
]

// Options
const backgroundOptions = [
  { value: 'white', label: 'White' },
  { value: 'surface', label: 'Light Gray' },
  { value: 'surface-sunken', label: 'Off-white' },
  { value: 'dark', label: 'Dark' }
]

const shadowOptions = [
  { value: 'none', label: 'None' },
  { value: 'sm', label: 'Small' },
  { value: 'md', label: 'Medium' },
  { value: 'lg', label: 'Large' },
  { value: 'xl', label: 'Extra Large' }
]

// Apply preset
async function applyPreset(presetName) {
  if (!apiEndpoints.apply_preset) {
    console.error('Apply preset endpoint not configured')
    return
  }

  isApplyingPreset.value = true
  try {
    const response = await fetch(apiEndpoints.apply_preset, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify({
        preset_name: presetName,
        target: 'header'
      })
    })

    if (!response.ok) {
      console.error(`Failed to apply preset: server returned ${response.status}`)
      return
    }

    const data = await response.json()
    if (data.success) {
      // Update local settings with preset values
      localSettings.value = { ...localSettings.value, ...data.settings }
      activePreset.value = presetName
      emitUpdate()
    } else {
      console.error('Failed to apply preset:', data.error)
    }
  } catch (error) {
    console.error('Error applying preset:', error)
  } finally {
    isApplyingPreset.value = false
  }
}

// Drag and drop
const { initSortable, reorderArray, generateId } = useDragDrop()

// Auto-populate syncing state
const isSyncing = ref(null)

// Toggle auto-populate for a nav item
async function toggleAutoPopulate(item) {
  if (!apiEndpoints.toggle_nav_auto_populate) {
    console.error('Toggle auto-populate endpoint not configured')
    return
  }

  // Initialize auto_populate_settings if needed
  if (item.auto_populate && !item.auto_populate_settings) {
    item.auto_populate_settings = {
      max_items: null,
      group_by_category: false,
      hidden_ids: []
    }
  }

  try {
    const response = await fetch(apiEndpoints.toggle_nav_auto_populate, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify({
        nav_item_id: item.id,
        enabled: !!item.auto_populate,
        source: item.auto_populate || 'services',
        settings: item.auto_populate_settings || {}
      })
    })

    if (!response.ok) {
      console.error(`Failed to toggle auto-populate: server returned ${response.status}`)
      return
    }

    const data = await response.json()
    if (data.success && data.nav_item) {
      // Update children if auto-populate was enabled
      if (data.nav_item.children) {
        item.children = data.nav_item.children
      }
      emitUpdate()
    }
  } catch (error) {
    console.error('Error toggling auto-populate:', error)
  }
}

// Sync nav services for a specific nav item
async function syncNavServices(item) {
  if (!apiEndpoints.sync_nav_services) {
    console.error('Sync nav services endpoint not configured')
    return
  }

  isSyncing.value = item.id
  try {
    const response = await fetch(apiEndpoints.sync_nav_services, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify({
        nav_item_id: item.id,
        source: item.auto_populate || 'services'
      })
    })

    if (!response.ok) {
      console.error(`Failed to sync nav services: server returned ${response.status}`)
      return
    }

    const data = await response.json()
    if (data.success) {
      item.children = data.children
      emitUpdate()
    } else {
      console.error('Failed to sync:', data.error)
    }
  } catch (error) {
    console.error('Error syncing nav services:', error)
  } finally {
    isSyncing.value = null
  }
}

// Default mega menu structure
function createDefaultMegaMenu() {
  return {
    columns: [
      {
        id: generateId(),
        title: 'Column 1',
        links: [{ id: generateId(), title: '', url: '/' }]
      }
    ],
    featured: {
      enabled: false,
      image_url: '',
      title: '',
      description: '',
      cta: { text: 'Learn More', url: '/' }
    },
    footer: {
      enabled: false,
      text: '',
      cta: { text: 'Contact Us', url: '/contact' }
    },
    layout: '3-col',
    width: 'container'
  }
}

// Handle dropdown mode change
function handleDropdownModeChange(item) {
  // Maintain backward compatibility with has_dropdown
  item.has_dropdown = item.dropdown_mode !== 'none'

  // Initialize mega_menu if switching to mega mode
  if (item.dropdown_mode === 'mega' && !item.mega_menu) {
    item.mega_menu = createDefaultMegaMenu()
  }

  // Initialize children array if switching to simple mode
  if (item.dropdown_mode === 'simple' && !item.children) {
    item.children = []
  }

  emitUpdate()
}

// Mega menu column management
function addMegaColumn(item) {
  if (!item.mega_menu) {
    item.mega_menu = createDefaultMegaMenu()
  }
  item.mega_menu.columns.push({
    id: generateId(),
    title: `Column ${item.mega_menu.columns.length + 1}`,
    links: []
  })
  emitUpdate()
}

function removeMegaColumn(item, columnIndex) {
  if (item.mega_menu && item.mega_menu.columns) {
    item.mega_menu.columns.splice(columnIndex, 1)
    emitUpdate()
  }
}

// Mega menu link management
function addMegaLink(item, columnIndex) {
  if (item.mega_menu && item.mega_menu.columns[columnIndex]) {
    if (!item.mega_menu.columns[columnIndex].links) {
      item.mega_menu.columns[columnIndex].links = []
    }
    item.mega_menu.columns[columnIndex].links.push({
      id: generateId(),
      title: '',
      url: '/',
      description: ''
    })
    emitUpdate()
  }
}

function removeMegaLink(item, columnIndex, linkIndex) {
  if (item.mega_menu && item.mega_menu.columns[columnIndex]) {
    item.mega_menu.columns[columnIndex].links.splice(linkIndex, 1)
    emitUpdate()
  }
}

// Add new nav item
function addNavItem() {
  if (!localContent.value.nav_items) {
    localContent.value.nav_items = []
  }
  localContent.value.nav_items.push({
    id: generateId(),
    title: '',
    label: '',
    url: '/',
    dropdown_mode: 'none',
    has_dropdown: false,
    auto_populate: null,
    auto_populate_settings: {},
    children: [],
    mega_menu: null
  })
  emitUpdate()
}

// Remove nav item
function removeNavItem(index) {
  localContent.value.nav_items.splice(index, 1)
  emitUpdate()
}

// Update setting
function updateSetting(key, value) {
  localSettings.value[key] = value
  emitUpdate()
}

// Emit updates to parent
function emitUpdate() {
  emit('update:content', {
    ...localContent.value,
    announcement_bar: { ...announcementBar.value },
    mobile: { ...mobileSettings.value }
  })
  emit('update:settings', { ...localSettings.value })
  emit('update')
}

// Watch for template changes
watch(localTemplate, (newVal) => {
  emit('update:template', newVal)
  emit('update')
})

// Watch for prop changes
watch(() => props.content, (newVal) => {
  localContent.value = normalizeHeaderContent({ ...localContent.value, ...newVal })
}, { deep: true })

watch(() => props.settings, (newVal) => {
  localSettings.value = { ...localSettings.value, ...newVal }
}, { deep: true })

watch(() => props.template, (newVal) => {
  localTemplate.value = newVal
})

// Initialize sortable after mount
onMounted(() => {
  nextTick(() => {
    if (navItemsContainer.value) {
      initSortable(navItemsContainer.value, {
        onReorder: ({ oldIndex, newIndex }) => {
          localContent.value.nav_items = reorderArray(
            localContent.value.nav_items,
            oldIndex,
            newIndex
          )
          emitUpdate()
        }
      })
    }
  })
})
</script>

<style scoped>
.sortable-ghost {
  opacity: 0.4;
  background: #c8ebfb;
}

.sortable-chosen {
  background: #f3f4f6;
}
</style>
